import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { VisibilitySection } from "@/components/recipe/form-sections/VisibilitySection";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

const formSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string(),
  visibility: z.enum(["public", "subscription"]),
  price: z.number().min(0),
  recipes: z.array(z.string()).min(1, "Please select at least one recipe"),
});

interface Recipe {
  id: string;
  title: string;
}

interface CreatePackageFormProps {
  initialData?: {
    id: string;
    title: string;
    description: string;
    visibility: "public" | "subscription";
    price: number;
    recipes: string[];
  };
}

export const CreatePackageForm = ({ initialData }: CreatePackageFormProps) => {
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: initialData?.title || "",
      description: initialData?.description || "",
      visibility: initialData?.visibility || "public",
      price: initialData?.price || 0,
      recipes: initialData?.recipes || [],
    },
  });

  useEffect(() => {
    const fetchRecipes = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          toast.error("Please sign in to create a package");
          navigate("/login");
          return;
        }

        const { data, error } = await supabase
          .from('recipes')
          .select('id, title')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching recipes:', error);
          toast.error("Failed to load recipes");
          return;
        }

        setRecipes(data || []);
      } catch (error) {
        console.error('Error in fetchRecipes:', error);
        toast.error("Failed to load recipes");
      }
    };

    fetchRecipes();
  }, [navigate]);

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Please sign in to create a package");
        return;
      }

      const { error } = await supabase
        .from('packages')
        .upsert({
          ...(initialData?.id ? { id: initialData.id } : {}),
          title: values.title,
          description: values.description,
          visibility: values.visibility,
          price: values.price,
          recipes: values.recipes,
          user_id: user.id,
        });

      if (error) throw error;

      toast.success(initialData ? "Package updated successfully!" : "Package created successfully!");
      navigate("/dashboard");
    } catch (error) {
      console.error('Error saving package:', error);
      toast.error(initialData ? "Failed to update package" : "Failed to create package");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Package Title</FormLabel>
              <FormControl>
                <Input placeholder="Enter package title" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Describe your package..."
                  className="resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="recipes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Select Recipes</FormLabel>
              <div className="mt-2 space-y-3">
                {recipes.map((recipe) => (
                  <div key={recipe.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={recipe.id}
                      checked={field.value?.includes(recipe.id)}
                      onCheckedChange={(checked) => {
                        const newValue = checked
                          ? [...field.value, recipe.id]
                          : field.value.filter((id) => id !== recipe.id);
                        field.onChange(newValue);
                      }}
                    />
                    <label
                      htmlFor={recipe.id}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {recipe.title}
                    </label>
                  </div>
                ))}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="visibility"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Visibility</FormLabel>
              <FormControl>
                <VisibilitySection
                  visibility={field.value}
                  setVisibility={(value) => field.onChange(value)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="price"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Package Price (USD)</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  {...field}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                  disabled={form.watch("visibility") === "public"}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/dashboard")}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? "Creating..." : "Create Package"}
          </Button>
        </div>
      </form>
    </Form>
  );
};
